import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import {
  Award,
  Shield,
  Building2,
  Target,
  Lightbulb,
  Heart,
  Sparkles,
  FileText,
  CheckCircle2,
  Globe,
  FileCheck,
  Lock,
  ExternalLink,
  Network,
  TrendingUp,
  Users,
  Briefcase,
  Landmark,
} from 'lucide-react';
import { Section, Container } from '@/components/ui/Section';
import { Card } from '@/components/ui/Card';
import { motion } from 'framer-motion';

export function About() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  const timelineEvents = [
    {
      year: '2009',
      title: t('about.timeline.events.2009.title'),
      description: t('about.timeline.events.2009.description'),
      icon: Building2,
      color: 'from-fuchsia-400 to-pink-500',
    },
    {
      year: '2015',
      title: t('about.timeline.events.2015.title'),
      description: t('about.timeline.events.2015.description'),
      icon: Sparkles,
      color: 'from-fuchsia-400 to-pink-500',
    },
    {
      year: '2018',
      title: t('about.timeline.events.2018.title'),
      description: t('about.timeline.events.2018.description'),
      icon: FileText,
      color: 'from-fuchsia-400 to-pink-500',
    },
    {
      year: '2021',
      title: t('about.timeline.events.2021.title'),
      description: t('about.timeline.events.2021.description'),
      icon: Award,
      color: 'from-fuchsia-400 to-pink-500',
    },
    {
      year: '2023',
      title: t('about.timeline.events.2023.title'),
      description: t('about.timeline.events.2023.description'),
      icon: Globe,
      color: 'from-fuchsia-400 to-pink-500',
    },
    {
      year: '2025',
      title: t('about.timeline.events.2025.title'),
      description: t('about.timeline.events.2025.description'),
      icon: Shield,
      color: 'from-fuchsia-400 to-pink-500',
    },
  ];

  const businessUnits = [
    {
      id: '01sistemi',
      name: '01Sistemi',
      tagline: t('about.businessUnits.sistemi.tagline'),
      description: t('about.businessUnits.sistemi.description'),
      icon: Building2,
      logo: '/media/products/01sistemi.png',
      color: 'from-cyan-400 to-cyan-600',
      bgColor: 'bg-cyan-50',
      textColor: 'text-cyan-500',
      borderColor: 'border-cyan-200',
      features: t('about.businessUnits.sistemi.features', { returnObjects: true }) as string[],
    },
    {
      id: '01semplice',
      name: '01Semplice',
      tagline: t('about.businessUnits.semplice.tagline'),
      description: t('about.businessUnits.semplice.description'),
      icon: Sparkles,
      logo: '/media/products/01semplice.png',
      color: 'from-emerald-400 to-emerald-600',
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-500',
      borderColor: 'border-emerald-200',
      features: t('about.businessUnits.semplice.features', { returnObjects: true }) as string[],
    },
    {
      id: 'firmaspid',
      name: 'FirmaSPID',
      tagline: t('about.businessUnits.firmaspid.tagline'),
      description: t('about.businessUnits.firmaspid.description'),
      icon: FileText,
      logo: '/media/products/firmaspid.png',
      color: 'from-orange-500 to-red-500',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-500',
      borderColor: 'border-orange-200',
      features: t('about.businessUnits.firmaspid.features', { returnObjects: true }) as string[],
    },
  ];

  const values = [
    {
      icon: Lightbulb,
      title: t('about.values.innovation.title'),
      description: t('about.values.innovation.description'),
      color: 'text-amber-500',
      bgColor: 'bg-amber-50',
    },
    {
      icon: Target,
      title: t('about.values.quality.title'),
      description: t('about.values.quality.description'),
      color: 'text-pink-500',
      bgColor: 'bg-pink-50',
    },
    {
      icon: Shield,
      title: t('about.values.security.title'),
      description: t('about.values.security.description'),
      color: 'text-rose-500',
      bgColor: 'bg-rose-50',
    },
    {
      icon: Heart,
      title: t('about.values.passion.title'),
      description: t('about.values.passion.description'),
      color: 'text-pink-500',
      bgColor: 'bg-pink-50',
    },
  ];

  const stats = [
    { value: '15+', label: t('about.stats.experience') },
    { value: '3', label: t('about.stats.businessUnits') },
    { value: '4', label: t('about.stats.companies') },
  ];

  return (
    <>
      <Helmet>
        <title>{t('about.pageTitle')}</title>
        <meta name="description" content={t('about.metaDescription')} />
        <link rel="canonical" href={`https://www.01sistemi.it/${currentLang}/chi-siamo`} />
      </Helmet>

      {/* Hero Section - Stile FAQ (Rosa Gradient) */}
      <Section className="relative overflow-hidden" padding="lg">
        {/* Pink glass background layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-pink-100/80 via-rose-50/90 to-pink-50/80" />
        <div className="absolute top-0 left-0 w-full h-full bg-white/30 backdrop-blur-sm" />

        {/* Decorative glass blobs */}
        <div className="absolute top-10 left-[10%] w-72 h-72 bg-pink-300/40 rounded-full blur-[100px]" />
        <div className="absolute top-20 right-[15%] w-96 h-96 bg-rose-300/30 rounded-full blur-[120px]" />
        <div className="absolute bottom-10 left-[30%] w-80 h-80 bg-fuchsia-200/30 rounded-full blur-[100px]" />
        <div className="absolute top-1/2 right-[5%] w-64 h-64 bg-pink-400/20 rounded-full blur-[80px]" />

        <Container className="relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Gruppo Maggioli Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pink-400/10 backdrop-blur-md border border-pink-200/50 mb-6">
                <Building2 className="w-4 h-4 text-pink-600" />
                <span className="text-pink-600 text-sm font-medium">{t('about.badge')}</span>
              </div>

              <h1 className="heading-1 text-black mb-6">
                {t('about.heroTitle')}
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-rose-500">
                  {t('about.heroHighlight')}
                </span>
              </h1>

              <p className="text-xl text-black/80 mb-8 leading-relaxed">
                {t('about.heroDescription')}
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-4">
                {stats.map((stat, i) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + i * 0.1 }}
                    className="text-center p-4 rounded-2xl bg-white/70 backdrop-blur-md border border-white/50 shadow-lg"
                  >
                    <div className="text-2xl font-bold text-pink-500">{stat.value}</div>
                    <div className="text-xs text-black/60">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right - Team Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative bg-white/70 backdrop-blur-md rounded-3xl p-4 border border-white/50 shadow-xl">
                <img
                  src="/media/pexels-jopwell-2422293.jpg"
                  alt="Team 01Sistemi"
                  className="w-full h-auto rounded-2xl shadow-lg"
                />

                {/* Floating badge */}
                <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-xl p-4 border border-pink-100">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-pink-100 flex items-center justify-center">
                      <Heart className="w-6 h-6 text-pink-500" />
                    </div>
                    <div>
                      <div className="text-lg font-bold text-gray-900">{t('about.hero.simplify')}</div>
                      <div className="text-xs text-gray-500">{t('about.hero.simplifySub')}</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </Container>
      </Section>

      {/* Business Units Detail Section - Pink Glassmorphism */}
      <Section className="relative overflow-hidden" padding="lg">
        {/* Pink glass background */}
        <div className="absolute inset-0 bg-gradient-to-br from-white via-pink-50/50 to-rose-50/80" />
        <div className="absolute inset-0 bg-white/40 backdrop-blur-sm" />
        {/* Decorative blur blobs */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-pink-200/30 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-rose-200/30 rounded-full blur-[100px]" />
        <Container className="relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="caption text-pink-500">{t('about.businessUnits.overline')}</span>
            <h2 className="heading-2 mt-3">{t('about.businessUnits.title')}</h2>
            <p className="body-regular mt-4 text-gray-600">
              {t('about.businessUnits.description')}
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {businessUnits.map((bu, index) => (
              <motion.div
                key={bu.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
              >
                <Card
                  variant="default"
                  className={`h-full overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 bg-white/80 backdrop-blur-md border ${bu.borderColor}`}
                >
                  {/* Header */}
                  <div className={`h-2 bg-gradient-to-r ${bu.color}`} />
                  <div className="p-8">
                    {/* Logo */}
                    <div className="mb-6">
                      <img
                        src={bu.logo}
                        alt={`${bu.name} Logo`}
                        className="h-16 w-auto object-contain"
                      />
                    </div>

                    <span className={`text-sm font-semibold ${bu.textColor} uppercase tracking-wider`}>
                      {bu.tagline}
                    </span>

                    <p className="body-regular mt-4 text-gray-600">{bu.description}</p>

                    {/* Features */}
                    <div className="mt-6 space-y-3">
                      {bu.features.map((feature) => (
                        <div key={feature} className="flex items-center gap-3">
                          <CheckCircle2 className={`w-5 h-5 ${bu.textColor}`} />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Timeline Section */}
      <Section padding="lg" className="bg-gradient-to-br from-pink-50 via-rose-50 to-pink-100 relative overflow-hidden">
        {/* Glassmorphism decorative elements */}
        <div className="absolute top-20 left-10 w-64 h-64 bg-pink-300/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-rose-300/20 rounded-full blur-3xl" />

        <Container className="relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="caption text-pink-500">{t('about.timeline.overline')}</span>
            <h2 className="heading-2 mt-3">{t('about.timeline.title')}</h2>
            <p className="body-regular mt-4 text-gray-600">
              {t('about.timeline.description')}
            </p>
          </div>

          <div className="relative max-w-4xl mx-auto">
            {/* Vertical Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-pink-300 via-rose-300 to-pink-400 rounded-full hidden md:block" />

            <div className="space-y-12">
              {timelineEvents.map((event, index) => (
                <motion.div
                  key={event.year}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative flex items-center ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  } flex-col md:gap-8`}
                >
                  {/* Content */}
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'} text-center`}>
                    <Card variant="default" className="inline-block p-6 hover:shadow-xl transition-shadow bg-white/80 backdrop-blur-md">
                      <span className={`inline-block px-3 py-1 rounded-full text-white text-sm font-bold bg-gradient-to-r ${event.color} mb-3`}>
                        {event.year}
                      </span>
                      <h3 className="heading-4 mb-2">{event.title}</h3>
                      <p className="body-small text-gray-600">{event.description}</p>
                    </Card>
                  </div>

                  {/* Center Icon */}
                  <div className="relative z-10 my-4 md:my-0">
                    <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${event.color} flex items-center justify-center shadow-lg`}>
                      <event.icon className="w-7 h-7 text-white" />
                    </div>
                  </div>

                  {/* Empty space for alternating layout */}
                  <div className="flex-1 hidden md:block" />
                </motion.div>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* Values Section - Pink Glassmorphism */}
      <Section className="relative overflow-hidden" padding="lg">
        {/* Pink gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-pink-100 via-rose-50 to-pink-50" />
        <div className="absolute inset-0 bg-white/40 backdrop-blur-sm" />

        {/* Decorative blur blobs */}
        <div className="absolute top-10 left-[10%] w-72 h-72 bg-pink-300/30 rounded-full blur-[100px]" />
        <div className="absolute bottom-10 right-[15%] w-96 h-96 bg-rose-300/25 rounded-full blur-[120px]" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-fuchsia-200/20 rounded-full blur-[100px]" />

        <Container className="relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pink-400/10 text-pink-600 text-sm font-medium backdrop-blur-sm border border-pink-200/50 mb-6">
              <Heart className="w-4 h-4" />
              {t('about.values.overline')}
            </span>
            <h2 className="heading-2">{t('about.values.title')}</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card
                  variant="default"
                  className="h-full bg-white/70 backdrop-blur-md border-white/50 hover:bg-white/80 hover:shadow-xl transition-all"
                >
                  <div className="p-6 text-center">
                    <div className={`w-14 h-14 rounded-2xl ${value.bgColor} flex items-center justify-center mx-auto mb-4`}>
                      <value.icon className={`w-7 h-7 ${value.color}`} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Certificazioni Section - Pink Glassmorphism */}
      <Section className="relative overflow-hidden" padding="lg">
        {/* Pink glass background */}
        <div className="absolute inset-0 bg-gradient-to-br from-rose-50/80 via-pink-50/90 to-white" />
        <div className="absolute inset-0 bg-white/30 backdrop-blur-sm" />
        {/* Decorative blur blobs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-pink-300/25 rounded-full blur-[100px]" />
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-rose-300/25 rounded-full blur-[100px]" />
        <Container className="relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="caption text-pink-500">{t('about.certifications.overline', 'Le nostre certificazioni')}</span>
            <h2 className="heading-2 mt-3">
              {t('about.certifications.title', 'Qualità e Sicurezza')}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-rose-500"> {t('about.certifications.certified', 'Certificata')}</span>
            </h2>
            <p className="body-regular mt-4 text-gray-600">
              {t('about.certifications.description', 'Standard internazionali per garantire eccellenza e affidabilità nei nostri servizi')}
            </p>
          </div>

          {/* Certificazioni Grid - Tutte rosa */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {/* ISO 9001 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="group relative"
            >
              <div className="relative h-full bg-white/80 backdrop-blur-md rounded-2xl p-5 border border-pink-200/50 shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 overflow-hidden">
                {/* Decorative gradient */}
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-pink-400/20 to-rose-400/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />

                {/* Badge */}
                <div className="absolute top-3 right-3">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-pink-100 text-pink-600 text-xs font-semibold">
                    <Award className="w-3 h-3" />
                    {t('about.certifications.badge', 'Cert.')}
                  </span>
                </div>

                <div className="relative">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-100 to-pink-200 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <FileCheck className="w-6 h-6 text-pink-600" />
                  </div>

                  <h4 className="font-bold text-gray-900 mb-1">{t('about.certifications.iso9001.title', 'ISO 9001')}</h4>
                  <p className="text-xs text-gray-500 mb-3">{t('about.certifications.iso9001.subtitle', 'Sistema di Gestione Qualità')}</p>

                  <a
                    href="/media/certificazioni/01Sistemi-Certificato-ISO-9001.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-pink-600 hover:text-pink-700 transition-colors"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    {t('about.certifications.view', 'Visualizza')}
                  </a>
                </div>
              </div>
            </motion.div>

            {/* ISO 27001 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="group relative"
            >
              <div className="relative h-full bg-white/80 backdrop-blur-md rounded-2xl p-5 border border-pink-200/50 shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 overflow-hidden">
                {/* Decorative gradient */}
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-pink-400/20 to-rose-400/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />

                {/* Badge */}
                <div className="absolute top-3 right-3">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-pink-100 text-pink-600 text-xs font-semibold">
                    <Shield className="w-3 h-3" />
                    {t('about.certifications.badge', 'Cert.')}
                  </span>
                </div>

                <div className="relative">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-100 to-pink-200 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Lock className="w-6 h-6 text-pink-600" />
                  </div>

                  <h4 className="font-bold text-gray-900 mb-1">{t('about.certifications.iso27001.title', 'ISO 27001')}</h4>
                  <p className="text-xs text-gray-500 mb-3">{t('about.certifications.iso27001.subtitle', 'Sicurezza Informazioni')}</p>

                  <a
                    href="/media/certificazioni/01Sistemi-Certificato-ISO-27001.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-pink-600 hover:text-pink-700 transition-colors"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    {t('about.certifications.view', 'Visualizza')}
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Politica Qualità */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="group relative"
            >
              <div className="relative h-full bg-white/80 backdrop-blur-md rounded-2xl p-5 border border-pink-200/50 shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 overflow-hidden">
                {/* Decorative gradient */}
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-pink-400/20 to-rose-400/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />

                {/* Badge */}
                <div className="absolute top-3 right-3">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-pink-100 text-pink-600 text-xs font-semibold">
                    <Target className="w-3 h-3" />
                    {t('about.certifications.policyBadge', 'Policy')}
                  </span>
                </div>

                <div className="relative">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-100 to-pink-200 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <FileText className="w-6 h-6 text-pink-600" />
                  </div>

                  <h4 className="font-bold text-gray-900 mb-1 leading-tight">{t('about.certifications.qualityPolicy.title', 'Politica Qualità')}</h4>
                  <p className="text-xs text-gray-500 mb-3">{t('about.certifications.qualityPolicy.subtitle', "Impegno per l'eccellenza")}</p>

                  <a
                    href="/media/certificazioni/01Sistemi-Politica-della-Qualita.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-pink-600 hover:text-pink-700 transition-colors"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    {t('about.certifications.view', 'Visualizza')}
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Politica Sicurezza */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="group relative"
            >
              <div className="relative h-full bg-white/80 backdrop-blur-md rounded-2xl p-5 border border-pink-200/50 shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 overflow-hidden">
                {/* Decorative gradient */}
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-pink-400/20 to-rose-400/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />

                {/* Badge */}
                <div className="absolute top-3 right-3">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-pink-100 text-pink-600 text-xs font-semibold">
                    <Shield className="w-3 h-3" />
                    {t('about.certifications.policyBadge', 'Policy')}
                  </span>
                </div>

                <div className="relative">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-100 to-pink-200 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Shield className="w-6 h-6 text-pink-600" />
                  </div>

                  <h4 className="font-bold text-gray-900 mb-1 leading-tight">{t('about.certifications.securityPolicy.title', 'Politica Sicurezza')}</h4>
                  <p className="text-xs text-gray-500 mb-3">{t('about.certifications.securityPolicy.subtitle', 'Protezione dei dati')}</p>

                  <a
                    href="/media/certificazioni/01Sistemi-Politica-per-la-Sicurezza-delle-Informazioni.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-pink-600 hover:text-pink-700 transition-colors"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    {t('about.certifications.view', 'Visualizza')}
                  </a>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Badge ACN sotto */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="mt-8 max-w-2xl mx-auto p-4 rounded-xl bg-gradient-to-r from-rose-50 to-pink-50 border border-rose-200/50"
          >
            <div className="flex items-center justify-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-white shadow-md flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-rose-500" />
              </div>
              <div>
                <div className="font-bold text-gray-900">ACN</div>
                <div className="text-sm text-gray-600">{t('about.gruppoMaggioli.certifications.acn')}</div>
              </div>
            </div>
          </motion.div>
        </Container>
      </Section>

      {/* Gruppo Maggioli Section - Stile Coerente */}
      <Section className="relative overflow-hidden" padding="lg">
        {/* Pink glass background - coerente con le altre sezioni */}
        <div className="absolute inset-0 bg-gradient-to-br from-pink-100/80 via-rose-50/90 to-pink-50/80" />
        <div className="absolute inset-0 bg-white/40 backdrop-blur-sm" />

        {/* Decorative blur blobs */}
        <div className="absolute top-10 left-[10%] w-72 h-72 bg-pink-300/30 rounded-full blur-[100px]" />
        <div className="absolute bottom-10 right-[15%] w-96 h-96 bg-rose-300/25 rounded-full blur-[120px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-fuchsia-200/20 rounded-full blur-[100px]" />

        <Container className="relative z-10">
          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pink-400/10 backdrop-blur-md border border-pink-200/50 mb-6"
            >
              <Network className="w-4 h-4 text-pink-600" />
              <span className="caption text-pink-600">{t('about.gruppoMaggioli.overline')}</span>
            </motion.div>

            <h2 className="heading-2 mb-6">
              {t('about.gruppoMaggioli.title')}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-rose-500">
                {t('about.gruppoMaggioli.titleHighlight')}
              </span>
            </h2>

            <p className="body-regular text-gray-600 max-w-3xl mx-auto">
              {t('about.gruppoMaggioli.description')}
            </p>
          </motion.div>

          {/* Stats Ecosistema */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12 max-w-4xl mx-auto"
          >
            {[
              { value: '15+', label: t('about.gruppoMaggioli.stats.yearsExperience'), icon: TrendingUp },
              { value: '4', label: t('about.gruppoMaggioli.stats.companies'), icon: Building2 },
              { value: '3', label: t('about.gruppoMaggioli.stats.businessUnits'), icon: Briefcase },
              { value: '500+', label: t('about.gruppoMaggioli.stats.satisfiedCustomers'), icon: Users },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 + index * 0.1 }}
              >
                <Card className="h-full text-center p-5 bg-white/70 backdrop-blur-md border-white/50 hover:bg-white/80 hover:shadow-xl transition-all duration-300">
                  <stat.icon className="w-6 h-6 text-pink-500 mb-2 mx-auto" />
                  <div className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</div>
                  <div className="text-xs text-gray-600">{stat.label}</div>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          {/* Companies Cards */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="mb-10"
          >
            <h3 className="text-center caption text-pink-500 mb-6">
              {t('about.gruppoMaggioli.companiesTitle')}
            </h3>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
              {[
                {
                  name: '01S s.r.l.',
                  piva: '02046890519',
                  roleKey: 'about.gruppoMaggioli.companies.holding.role',
                  descKey: 'about.gruppoMaggioli.companies.holding.desc',
                  gradient: 'from-amber-400 to-orange-500',
                  lightColor: 'bg-amber-50',
                  textColor: 'text-amber-600',
                  borderColor: 'border-amber-200',
                },
                {
                  name: '01Sistemi',
                  piva: '01935250504',
                  roleKey: 'about.gruppoMaggioli.companies.sistemi.role',
                  descKey: 'about.gruppoMaggioli.companies.sistemi.desc',
                  gradient: 'from-info-400 to-info-600',
                  lightColor: 'bg-info-50',
                  textColor: 'text-info-600',
                  borderColor: 'border-info-200',
                },
                {
                  name: '01Semplice',
                  piva: '03469160547',
                  roleKey: 'about.gruppoMaggioli.companies.semplice.role',
                  descKey: 'about.gruppoMaggioli.companies.semplice.desc',
                  gradient: 'from-accent-400 to-accent-600',
                  lightColor: 'bg-accent-50',
                  textColor: 'text-accent-600',
                  borderColor: 'border-accent-200',
                },
                {
                  name: 'FirmaSpid',
                  piva: '03633690544',
                  roleKey: 'about.gruppoMaggioli.companies.firma.role',
                  descKey: 'about.gruppoMaggioli.companies.firma.desc',
                  gradient: 'from-cta-400 to-cta-600',
                  lightColor: 'bg-cta-50',
                  textColor: 'text-cta-600',
                  borderColor: 'border-cta-200',
                },
              ].map((company, index) => (
                <motion.div
                  key={company.piva}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                >
                  <Card className="h-full overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 bg-white/80 backdrop-blur-md border-white/50">
                    {/* Gradient top bar */}
                    <div className={`h-1.5 bg-gradient-to-r ${company.gradient}`} />

                    <div className="p-5">
                      {/* Content */}
                      <h4 className="font-bold text-gray-900 mb-1">{company.name}</h4>
                      <div className="text-xs text-gray-500 mb-3">P.IVA {company.piva}</div>
                      <div className={`text-xs font-medium ${company.textColor} ${company.lightColor} px-2 py-1 rounded-md inline-block mb-2 border ${company.borderColor}`}>
                        {t(company.roleKey)}
                      </div>
                      <p className="text-sm text-gray-600">{t(company.descKey)}</p>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Trust Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
            className="flex justify-center"
          >
            <div className="inline-flex items-center gap-4 bg-white/70 backdrop-blur-md rounded-2xl px-6 py-3 border border-pink-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-pink-100 flex items-center justify-center">
                  <Landmark className="w-5 h-5 text-pink-600" />
                </div>
                <div className="text-left">
                  <div className="text-sm font-semibold text-gray-900">{t('about.gruppoMaggioli.trustBadge.title', 'Trasparenza Totale')}</div>
                  <div className="text-xs text-gray-500">{t('about.gruppoMaggioli.trustBadge.description', 'Tutti i dati societari pubblici')}</div>
                </div>
              </div>
              <div className="h-8 w-px bg-pink-200" />
              <div className="flex items-center gap-2 text-pink-600">
                <CheckCircle2 className="w-4 h-4" />
                <span className="text-xs font-medium">{t('about.gruppoMaggioli.trustBadge.compliant', '100% Compliant')}</span>
              </div>
            </div>
          </motion.div>
        </Container>
      </Section>

    </>
  );
}
